import { useState } from "react";
import { Menu, X } from "lucide-react";
import useUserStore from "../stores/user.store.ts";
import { MaterialTypeEnum } from "@ns/types";
import { useNavigate, useLocation } from "react-router-dom";

export const HeaderComponent = () => {
  const { materialFilter, setMaterialFilter } = useUserStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is on a single page (any path that's not the root)
  const isOnSinglePage = location.pathname !== "/";

  const handleMaterialFilter = (filter: MaterialTypeEnum | null) => {
    setMaterialFilter(filter);

    // If on a single page, redirect to main page
    if (isOnSinglePage) {
      navigate("/");
    }

    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      <header className="hidden md:flex items-center justify-between p-4 bg-white border-b border-[#d9d9d9]">
        <div className="flex items-center">
          <img
            src="/assets/radic.webp?height=40&width=120"
            alt="Mermeri Radic Logo"
            width={120}
            height={40}
            className="h-10 w-auto cursor-pointer"
            onClick={() => {
              setMaterialFilter(null);
              if (isOnSinglePage) navigate("/");
            }}
          />
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-4">
          <button
            className={`px-3 py-1 text-sm ${materialFilter === null ? "font-semibold text-[#2c2c2c]" : "text-[#b2b2b2]"}`}
            onClick={() => handleMaterialFilter(null)}
          >
            Svi materijali
          </button>
          <button
            className={`px-3 py-1 text-sm ${materialFilter === MaterialTypeEnum.MARBLE ? "font-semibold text-[#2c2c2c]" : "text-[#b2b2b2]"}`}
            onClick={() => handleMaterialFilter(MaterialTypeEnum.MARBLE)}
          >
            Mermer
          </button>
          <button
            className={`px-3 py-1 text-sm ${materialFilter === MaterialTypeEnum.QUARTZ ? "font-semibold text-[#2c2c2c]" : "text-[#b2b2b2]"}`}
            onClick={() => handleMaterialFilter(MaterialTypeEnum.QUARTZ)}
          >
            Kvarc
          </button>
          <button
            className={`px-3 py-1 text-sm ${materialFilter === MaterialTypeEnum.TRAVERTINE ? "font-semibold text-[#2c2c2c]" : "text-[#b2b2b2]"}`}
            onClick={() => handleMaterialFilter(MaterialTypeEnum.TRAVERTINE)}
          >
            Travertin
          </button>
          <button
            className={`px-3 py-1 text-sm ${materialFilter === MaterialTypeEnum.ONYX ? "font-semibold text-[#2c2c2c]" : "text-[#b2b2b2]"}`}
            onClick={() => handleMaterialFilter(MaterialTypeEnum.ONYX)}
          >
            Onyx
          </button>
          <button
            className={`px-3 py-1 text-sm ${materialFilter === MaterialTypeEnum.GRANITE ? "font-semibold text-[#2c2c2c]" : "text-[#b2b2b2]"}`}
            onClick={() => handleMaterialFilter(MaterialTypeEnum.GRANITE)}
          >
            Granit
          </button>
          <button
            className={`px-3 py-1 text-sm ${materialFilter === MaterialTypeEnum.QUARZITE ? "font-semibold text-[#2c2c2c]" : "text-[#b2b2b2]"}`}
            onClick={() => handleMaterialFilter(MaterialTypeEnum.QUARZITE)}
          >
            Kvarcit
          </button>
        </nav>
      </header>

      {/* Mobile Header */}
      <header className="md:hidden sticky top-0 z-10 flex items-center justify-between p-4 bg-white border-b border-[#d9d9d9]">
        <div className="flex items-center">
          <img
            src="/assets/radic.webp?height=40&width=120"
            alt="Mermeri Radic Logo"
            width={120}
            height={40}
            className="h-10 w-auto cursor-pointer"
            onClick={() => {
              setMaterialFilter(null);
              if (isOnSinglePage) navigate("/");
            }}
          />
        </div>
        <button
          className="p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <Menu className="w-6 h-6 text-[#2c2c2c]" />
        </button>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-white pt-16">
          <button
            className="absolute top-4 right-4 p-2"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X className="w-6 h-6 text-[#2c2c2c]" />
          </button>

          <div className="flex flex-col items-center space-y-4 p-4">
            <button
              className={`px-3 py-2 w-full text-center ${materialFilter === null ? "font-semibold bg-[#f5f5f5] text-black" : "text-[#d3d3d3]"}`}
              onClick={() => handleMaterialFilter(null)}
            >
              Svi materijali
            </button>
            <button
              className={`px-3 py-2 w-full text-center ${materialFilter === MaterialTypeEnum.MARBLE ? "font-semibold bg-[#f5f5f5] text-black" : "text-[#d3d3d3]"}`}
              onClick={() => handleMaterialFilter(MaterialTypeEnum.MARBLE)}
            >
              Mermer
            </button>
            <button
              className={`px-3 py-2 w-full text-center ${materialFilter === MaterialTypeEnum.QUARTZ ? "font-semibold bg-[#f5f5f5] text-black" : "text-[#d3d3d3]"}`}
              onClick={() => handleMaterialFilter(MaterialTypeEnum.QUARTZ)}
            >
              Kvarc
            </button>
            <button
              className={`px-3 py-2 w-full text-center ${materialFilter === MaterialTypeEnum.TRAVERTINE ? "font-semibold bg-[#f5f5f5] text-black" : "text-[#d3d3d3]"}`}
              onClick={() => handleMaterialFilter(MaterialTypeEnum.TRAVERTINE)}
            >
              Travertin
            </button>
            <button
              className={`px-3 py-2 w-full text-center ${materialFilter === MaterialTypeEnum.ONYX ? "font-semibold bg-[#f5f5f5] text-black" : "text-[#d3d3d3]"}`}
              onClick={() => handleMaterialFilter(MaterialTypeEnum.ONYX)}
            >
              Onyx
            </button>
            <button
              className={`px-3 py-2 w-full text-center ${materialFilter === MaterialTypeEnum.GRANITE ? "font-semibold bg-[#f5f5f5] text-black" : "text-[#d3d3d3]"}`}
              onClick={() => handleMaterialFilter(MaterialTypeEnum.GRANITE)}
            >
              Granit
            </button>
            <button
              className={`px-3 py-2 w-full text-center ${materialFilter === MaterialTypeEnum.QUARZITE ? "font-semibold bg-[#f5f5f5] text-black" : "text-[#d3d3d3]"}`}
              onClick={() => handleMaterialFilter(MaterialTypeEnum.QUARZITE)}
            >
              Kvarcit
            </button>
          </div>
        </div>
      )}
    </>
  );
};
